<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import WahaAPI from 'dashboard/api/waha';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const qrCodes = reactive({
  whatsapp: '',
  messenger: '',
  telegram: '',
});

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

// Use useInbox composable with the inbox ID
const {
  isAWhatsAppCloudChannel,
  isAnEmailChannel,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isATelegramChannel,
} = useInbox(route.params.inbox_id);

const hasDuplicateInstagramInbox = computed(() => {
  const instagramId = currentInbox.value.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return (
    currentInbox.value.channel_type === INBOX_TYPES.INSTAGRAM && facebookInbox
  );
});

const shouldShowWhatsAppWebhookDetails = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source !== 'embedded_signup'
  );
});

const isWhatsAppEmbeddedSignup = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source === 'embedded_signup'
  );
});

const message = computed(() => {
  if (isAWhatsAppCloudChannel.value && shouldShowWhatsAppWebhookDetails.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAnEmailChannel.value && !currentInbox.value.provider) {
    return t('INBOX_MGMT.ADD.EMAIL_CHANNEL.FINISH_MESSAGE');
  }

  if (currentInbox.value.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isWhatsAppEmbeddedSignup.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION'
    )}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

async function generateQRCode(platform, identifier) {
  if (!identifier || !identifier.trim()) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid identifier for ${platform} QR code`);
    return;
  }

  try {
    const platformUrls = {
      whatsapp: id => `https://wa.me/${id}`,
      messenger: id => `https://m.me/${id}`,
      telegram: id => `https://t.me/${id}`,
    };

    const url = platformUrls[platform](identifier);
    const qrDataUrl = await QRCode.toDataURL(url);
    qrCodes[platform] = qrDataUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error generating ${platform} QR code:`, error);
    qrCodes[platform] = '';
  }
}

// ─────────────────────────────────────────────────────────────
// WAHA real-QR flow (replaces the fake wa.me QR for WAHA inboxes)
// ─────────────────────────────────────────────────────────────

const isWahaInbox = computed(
  () => isAWhatsAppChannel.value && currentInbox.value?.provider === 'waha'
);

async function generateQRCodes() {
  if (!currentInbox.value) return;

  // WhatsApp (skip for WAHA inboxes — they get the real WAHA QR instead)
  if (
    currentInbox.value.phone_number &&
    isAWhatsAppChannel.value &&
    !isWahaInbox.value
  ) {
    const phoneNumber = currentInbox.value.phone_number.replace(
      'whatsapp:',
      ''
    );
    await generateQRCode('whatsapp', phoneNumber);
  }

  // Facebook Messenger
  if (currentInbox.value.page_id && isAFacebookInbox.value) {
    await generateQRCode('messenger', currentInbox.value.page_id);
  }

  // Telegram
  if (isATelegramChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('telegram', currentInbox.value.bot_name);
  }
}

const QR_SCAN_STATUSES = new Set([
  'scan_qr_code',
  'scan-qr-code',
  'scan qr code',
]);
const CONNECTED_STATUSES = new Set(['open', 'working', 'connected']);
const FAILED_STATUSES = new Set(['failed', 'stopped', 'close', 'closed']);

const wahaQrCode = ref(null);
const wahaStatus = ref(null);
const wahaPolling = ref(null);
const isFetchingWahaQr = ref(false);
const isRestartingWaha = ref(false);
const wahaAutoRestartAttempted = ref(false);
const wahaPollingStopped = ref(false);
let wahaTickCount = 0;

const wahaStatusLower = computed(() =>
  (wahaStatus.value || '').toString().toLowerCase()
);

const isWahaConnected = computed(() =>
  CONNECTED_STATUSES.has(wahaStatusLower.value)
);

const wahaShowQr = computed(() => QR_SCAN_STATUSES.has(wahaStatusLower.value));

const wahaShowRestart = computed(() =>
  FAILED_STATUSES.has(wahaStatusLower.value)
);

const wahaStatusLabel = computed(() => {
  const status = wahaStatusLower.value;
  switch (status) {
    case 'working':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.WORKING');
    case 'connected':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.CONNECTED');
    case 'open':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.OPEN');
    case 'close':
    case 'closed':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.CLOSE');
    case 'stopped':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.STOPPED');
    case 'starting':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.STARTING');
    case 'failed':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.FAILED');
    case 'connecting':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.CONNECTING');
    case 'scan_qr_code':
    case 'scan-qr-code':
    case 'scan qr code':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.SCAN_QR_CODE');
    default:
      break;
  }

  if (!status) return t('INBOX_MGMT.ADD.WAHA.STATUS.UNKNOWN');

  return status
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
});

async function toDataUrl(value) {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:image')) return trimmed;

  const compact = trimmed.replace(/\s+/g, '');
  const isBase64 = () => {
    try {
      return btoa(atob(compact)) === compact;
    } catch (error) {
      return false;
    }
  };

  if (isBase64()) {
    return `data:image/png;base64,${compact}`;
  }

  return QRCode.toDataURL(trimmed);
}

async function normalizeQrPayload(payload) {
  if (!payload) return null;
  if (typeof payload === 'string') return toDataUrl(payload);

  const candidate =
    payload.base64 ||
    payload.qrcode?.base64 ||
    payload.qrcode?.code ||
    payload.qrcode ||
    payload.code ||
    payload.value ||
    null;

  return toDataUrl(candidate);
}

async function fetchWahaStatus() {
  try {
    const data = await WahaAPI.fetchStatus(
      route.params.accountId,
      currentInbox.value.id
    );
    const session = data?.session;
    const rawStatus =
      data?.status ||
      session?.status ||
      session?.state ||
      session?.connectionStatus ||
      '';
    const next = rawStatus.toString().toLowerCase();
    // eslint-disable-next-line no-console
    console.debug('[WAHA Setup] fetchWahaStatus parsed', {
      raw: rawStatus,
      normalized: next || 'close',
      sessionName: session?.name,
    });
    wahaStatus.value = next || 'close';
    return wahaStatus.value;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[WAHA Setup] fetchWahaStatus FAILED', {
      message: error.message,
      httpStatus: error.httpStatus,
      upstreamStatus: error.upstreamStatus,
      backendError: error.backendError,
    });
    wahaStatus.value = 'close';
    return null;
  }
}

async function fetchWahaQr() {
  if (!isWahaInbox.value || !currentInbox.value?.id) return;
  isFetchingWahaQr.value = true;
  try {
    const payload = await WahaAPI.fetchQrCode(
      route.params.accountId,
      currentInbox.value.id
    );
    const normalized = await normalizeQrPayload(payload);
    // eslint-disable-next-line no-console
    console.debug('[WAHA Setup] fetchWahaQr parsed', {
      hasQr: Boolean(normalized),
      payloadType: typeof payload,
      payloadKeys:
        payload && typeof payload === 'object' ? Object.keys(payload) : null,
    });
    wahaQrCode.value = normalized;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[WAHA Setup] fetchWahaQr FAILED', {
      message: error.message,
      httpStatus: error.httpStatus,
      upstreamStatus: error.upstreamStatus,
      backendError: error.backendError,
    });
    wahaQrCode.value = null;
  } finally {
    isFetchingWahaQr.value = false;
  }
}

async function restartWahaSession() {
  if (!isWahaInbox.value || !currentInbox.value?.id) return;
  isRestartingWaha.value = true;
  try {
    await WahaAPI.restartSession(
      route.params.accountId,
      currentInbox.value.id
    );
    await fetchWahaStatus();
    if (wahaShowQr.value) await fetchWahaQr();
  } catch (error) {
    // noop — polling will reflect any persistent failure state
  } finally {
    isRestartingWaha.value = false;
  }
}

function stopWahaPolling() {
  if (wahaPolling.value) {
    clearInterval(wahaPolling.value);
    wahaPolling.value = null;
  }
}

async function wahaTick() {
  await fetchWahaStatus();

  if (isWahaConnected.value) {
    // eslint-disable-next-line no-console
    console.info('[WAHA Setup] connected — parando polling');
    stopWahaPolling();
    return;
  }

  // Status FAILED do WAHA: tenta restart automatico 1x. Se persistir,
  // para o polling pra UI ficar estavel com botao "Reiniciar sessao".
  if (wahaShowRestart.value) {
    if (!wahaAutoRestartAttempted.value) {
      wahaAutoRestartAttempted.value = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[WAHA Setup] sessao FAILED detectada — tentando restart automatico (1x)'
      );
      await restartWahaSession();
      // Continua o polling para captar o novo estado pos-restart.
    } else {
      // eslint-disable-next-line no-console
      console.error(
        '[WAHA Setup] sessao continua FAILED apos restart automatico — ' +
          'parando polling. Use o botao "Reiniciar sessao" ou recrie a ' +
          'inbox em Settings > Inboxes > Configuration.'
      );
      stopWahaPolling();
      wahaPollingStopped.value = true;
      return;
    }
  }

  if (wahaShowQr.value) {
    // WAHA rotates the QR ~30s. Refetch on the first tick + every 3rd
    // tick (~9s) to stay well under that window.
    if (wahaTickCount === 0 || wahaTickCount % 3 === 0) {
      await fetchWahaQr();
    }
  }

  wahaTickCount += 1;
}

function startWahaPolling() {
  if (!isWahaInbox.value || wahaPolling.value) return;
  wahaTickCount = 0;
  wahaAutoRestartAttempted.value = false;
  wahaPollingStopped.value = false;
  wahaTick();
  wahaPolling.value = setInterval(wahaTick, 3000);
}

async function manualRestartWaha() {
  wahaAutoRestartAttempted.value = false;
  wahaPollingStopped.value = false;
  await restartWahaSession();
  if (!wahaPolling.value) startWahaPolling();
}

// Observa só o id (primitivo) para nao disparar em re-emits do Vuex que mantem
// o mesmo inbox carregado. Se trocasse a inbox (id diferente), reseta o polling.
watch(
  () => currentInbox.value?.id,
  (newId, oldId) => {
    if (!newId || newId === oldId) return;
    stopWahaPolling();
    if (isWahaInbox.value) {
      startWahaPolling();
    } else {
      generateQRCodes();
    }
  }
);

onMounted(() => {
  if (isWahaInbox.value) {
    startWahaPolling();
  } else {
    generateQRCodes();
  }
});

onBeforeUnmount(() => {
  stopWahaPolling();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <DuplicateInboxBanner
      v-if="hasDuplicateInstagramInbox"
      :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
    />
    <EmptyState
      :title="$t('INBOX_MGMT.FINISH.TITLE')"
      :message="message"
      :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
    >
      <div class="w-full text-center">
        <div class="my-4 mx-auto max-w-[70%]">
          <woot-code
            v-if="currentInbox.web_widget_script"
            :script="currentInbox.web_widget_script"
          />
        </div>
        <div
          v-if="shouldShowWhatsAppWebhookDetails"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <p class="mt-8 font-medium text-slate-700 dark:text-slate-200">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
          </p>
          <woot-code lang="html" :script="currentInbox.callback_webhook_url" />
          <p class="mt-8 font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
              )
            }}
          </p>
          <woot-code
            lang="html"
            :script="currentInbox.provider_config.webhook_verify_token"
          />
        </div>
        <div
          v-if="isAnEmailChannel && !currentInbox.provider"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <woot-code lang="html" :script="currentInbox.forward_to_email" />
        </div>
        <div
          v-if="isAWhatsAppChannel && !isWahaInbox && qrCodes.whatsapp"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.whatsapp"
              alt="WhatsApp QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isWahaInbox"
          class="flex flex-col gap-3 items-center mx-auto mt-8 max-w-md"
        >
          <div class="flex gap-2 items-center text-sm">
            <span class="font-medium">
              {{ $t('INBOX_MGMT.ADD.WAHA.STATUS.LABEL') }}
            </span>
            <span
              class="font-semibold"
              :class="{
                'text-green-600': isWahaConnected,
                'text-yellow-600': ['connecting', 'starting'].includes(
                  wahaStatusLower
                ),
                'text-red-600': [
                  'close',
                  'closed',
                  'stopped',
                  'failed',
                ].includes(wahaStatusLower),
                'text-gray-600': !wahaStatusLower,
              }"
            >
              {{ wahaStatusLabel }}
            </span>
          </div>

          <div
            v-if="isWahaConnected"
            class="flex flex-col gap-2 items-center mt-2"
          >
            <fluent-icon
              icon="checkmark-circle"
              type="solid"
              size="48"
              class="text-green-600"
            />
            <p class="text-base font-medium text-green-700">
              {{ $t('INBOX_MGMT.FINISH.WAHA_CONNECTED') }}
            </p>
          </div>

          <template v-else-if="wahaShowQr && wahaQrCode">
            <p class="mt-2 text-sm text-center text-n-slate-9">
              {{ $t('INBOX_MGMT.FINISH.WAHA_QR_INSTRUCTION') }}
            </p>
            <div class="rounded-lg shadow outline-1 outline-n-strong outline">
              <img
                :src="wahaQrCode"
                alt="WhatsApp WAHA QR Code"
                class="rounded-lg size-48"
              />
            </div>
            <NextButton
              size="small"
              variant="link"
              :is-loading="isFetchingWahaQr"
              :label="$t('INBOX_MGMT.ADD.WAHA.QR_CODE.GENERATE')"
              @click="fetchWahaQr"
            />
          </template>

          <template v-else-if="wahaShowRestart">
            <div
              class="flex flex-col gap-3 items-center p-4 mt-2 max-w-md text-center bg-red-50 rounded-lg border border-red-200"
            >
              <fluent-icon
                icon="warning"
                type="solid"
                size="32"
                class="text-red-600"
              />
              <p class="font-medium text-red-700">
                {{ $t('INBOX_MGMT.FINISH.WAHA_RESTART_HINT') }}
              </p>
              <p
                v-if="wahaPollingStopped"
                class="text-xs text-n-slate-9"
              >
                {{ $t('INBOX_MGMT.FINISH.WAHA_RESTART_AUTO_FAILED') }}
              </p>
              <NextButton
                :is-loading="isRestartingWaha"
                :label="$t('INBOX_MGMT.ADD.WAHA.RECREATE.BUTTON')"
                @click="manualRestartWaha"
              />
            </div>
          </template>

          <template v-else>
            <p class="mt-2 text-sm text-center text-n-slate-9">
              {{ $t('INBOX_MGMT.FINISH.WAHA_WAITING') }}
            </p>
          </template>
        </div>
        <div
          v-if="isAFacebookInbox && qrCodes.messenger"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.MESSENGER_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.messenger"
              alt="Messenger QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isATelegramChannel && qrCodes.telegram"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.TELEGRAM_QR_INSTRUCTION') }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.telegram"
              alt="Telegram QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="!isWahaInbox || isWahaConnected"
          class="flex gap-2 justify-center mt-4"
        >
          <router-link
            :to="{
              name: 'settings_inbox_show',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              outline
              slate
              :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
            />
          </router-link>
          <router-link
            :to="{
              name: 'inbox_dashboard',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              solid
              teal
              :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
            />
          </router-link>
        </div>
      </div>
    </EmptyState>
  </div>
</template>
